/**
 * @author NikolaV
 */
import './LogpointsEditor2dMap.scss';
import * as React from 'react';
import { TreeNode } from '@shared/ui-native/Tree';
import { Drawer } from '@shared/ui-native/Drawer';
import { Events } from '@shared/Events';
import { Notify } from '@shared/ui-native/Notify';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { SelectionModePicker } from '@pages/map/actions/shared/SelectionModePicker';

import { AreaSelection } from '@pages/map/actions/shared/AreaSelection';
import { ClickSelection } from '@pages/map/actions/shared/ClickSelection';
import * as olStyle from 'ol/style';
import { LogpointsLayer } from '@openlayers/logpoints/LogpointsLayer';
import { LogpointsEditor } from '@shared/logpoints/ui/LogpointsEditor';
import { mapTools, mapUI, mapWrapper } from '../map';


type SelectMode = 'click' | 'area';

export const LogpointsEditor2dMap = {
    open(logpoints: ILogpoint[] = [],
        preselectApproval = false,
        selectMode: SelectMode = 'area',
        treeData: ITreeData) {

        const drawerElement = Drawer.open();

        if (!preselectApproval) {
            Events.trigger('drawer-open.editor');
        }

        ReactUtils.safeUnmountComponentAtNode(drawerElement);

        ReactUtils.safeRender(<EditPanel2dMap
            logpoints={logpoints}
            selectMode={preselectApproval ? 'area' : selectMode}
            treeData={treeData}
        />, drawerElement);
    },

    selectForApprovalOnMap(logpointsLayer: LogpointsLayer, treeData: ITreeData) {
        return new AreaSelection<ILogpoint>(logpointsLayer.layerSource, logpoints => {
            // filter out logpoint that are not visible, careful to not exclude culled logpoints, only those hidden by filter
            const visibleHitsData: ILogpoint[] = logpoints.filter(data => {
                const feature = logpointsLayer.getFeatureById(data.id);
                return logpointsLayer.featureMatchesFilter(feature);
            });

            if (logpoints.length) {
                LogpointsEditor2dMap.open(visibleHitsData, true, 'area', treeData);
            } else {
                Notify.warn(__('js_logpoints.no_logpoints_selected'));
            }
        });
    },

    close() {
        Drawer.close();
    },
};


class EditPanel2dMap extends React.Component<{
    logpoints: ILogpoint[],
    selectMode: SelectMode,
    treeData: ITreeData
}, {}> {
    state = {
        logpoints: this.props.logpoints,
        selectMode: this.props.selectMode,
    };

    private clickSelection: ClickSelection<ILogpoint>;
    private areaSelection: AreaSelection<ILogpoint>;

    private readonly selectedStyle = new olStyle.Style({
        image: new olStyle.Circle({
            radius: 13,
            fill: new olStyle.Fill({
                color: [255, 255, 255, 0.7],
            }),
            stroke: new olStyle.Stroke({
                color: [0, 0, 0, 1],
                width: 2,
            }),
        }),
    });

    onSaveSuccess = (updatedIds: number[], updatedLogpoints: ILogpoint[], formData: any) => {

        const checkedTreeNodes: TreeNode[] = mapUI.mapPanels.panels.logpoints.genericComponent.tree.getChecked();
        const checkedFolderIds: number[] = checkedTreeNodes.filter(n => n.isFolder()).map(f => f.id);
        const checkedModelIds: number[] = checkedTreeNodes.filter(n => n.isFile()).map(f => f.id);

        // update logpoint feature data so we can rerender
        for (const id of updatedIds) {
            const feature = mapWrapper.featureLayers.logpoints.getFeatureById(id);
            if (!feature) continue;

            const data: ILogpoint = feature.get('data');
            const source: FeatureSource = feature.get('source');

            if (formData.hasOwnProperty('approvalStage')) {
                data.approvalStage = updatedLogpoints.filter(l => l.id === id)[0].approvalStage;
                data.tol = updatedLogpoints.filter(l => l.id === id)[0].tol;
            }
            if (formData.hasOwnProperty('folderId')) {
                source.folderId = parseInt(formData.folderId);
                data.folderId = source.folderId;
            }
            if (formData.hasOwnProperty('modelId')) {
                source.modelId = parseInt(formData.modelId);
                data.modelId = source.modelId;
            }
            if (formData.hasOwnProperty('alignmentId')) {
                source.alignmentId = parseInt(formData.alignmentId);
                data.alignmentId = source.alignmentId;
            }
            if (formData.hasOwnProperty('code')) {
                data.code = formData.code;
            }
            if (formData.hasOwnProperty('vehicleId')) {
                data.vehicleId = formData.vehicleId;
            }
            if (formData.hasOwnProperty('pointName')) {
                data.pointName = formData.pointName;
            }
            if (formData.hasOwnProperty('number')) {
                data.number = formData.number;
            }
            if (formData.hasOwnProperty('linecode')) {
                data.linecode = formData.linecode;
            }
            if (formData.hasOwnProperty('surfacecode')) {
                data.surfacecode = formData.surfacecode;
            }

            feature.set('data', data);

            // remove logpoint feature if updated source is not selected in filetree
            const hasFolderChecked = source.hasOwnProperty('folderId') && checkedFolderIds.indexOf(source.folderId) !== -1;
            const hasModelChecked = source.hasOwnProperty('modelId') && checkedModelIds.indexOf(source.modelId) !== -1;

            if (!(hasFolderChecked || hasModelChecked)) {
                if (!feature.get('forceShow')) { // forceShow=true -> logpoint was loaded through vehicle info dialog, we dont want to ever remove it until dialog is closed
                    mapWrapper.featureLayers.logpoints.removeFeatureById(id);
                }
            }

            mapWrapper.featureLayers.logpoints.onLayerSourceChanged();
        }

        mapUI.mapPanels.panels.logpoints.genericComponent.renderLogpointFolderCounts();
        mapWrapper.featureLayers.logpoints.layerSource.dispatchEvent('change');
        mapWrapper.featureLayers.logpoints.onLayerSourceChanged();
        if (this.state.selectMode == 'click') {
            this.clickSelection.clear();
            this.clickSelection.select(updatedLogpoints);
        }
    };

    onDeleteSuccess = (deletedIds: number[]) => {
        deletedIds.forEach(id => mapWrapper.featureLayers.logpoints.removeFeatureById(id));
        mapUI.mapPanels.panels.logpoints.genericComponent.renderLogpointFolderCounts();
        this.clickSelection.clear();
        this.areaSelection.clear();
        this.setState({
            logpoints: [],
        });
    };

    onLogpointsSelected = (logpoints: ILogpoint[]) => {
        // filter out logpoint that are not visible, careful to not exclude culled logpoints, only those hidden by filter
        const visibleHitsData: ILogpoint[] = logpoints.filter(data => {
            const feature = mapWrapper.featureLayers.logpoints.getFeatureById(data.id);
            return mapWrapper.featureLayers.logpoints.featureMatchesFilter(feature);
        });

        if (visibleHitsData.length === 0) {
            Notify.warn(__('js_logpoints.no_logpoints_selected'));
            this.areaSelection.clear();
        }

        this.setState({
            logpoints: visibleHitsData,
        });

        const visibleFeatures = visibleHitsData.map(data => mapWrapper.featureLayers.logpoints.getFeatureById(data.id));
        mapWrapper.featureLayers.logpoints.highlightFeatures(visibleFeatures);
    };

    componentDidMount() {
        this.clickSelection = new ClickSelection(mapWrapper.featureLayers.logpoints, this.onLogpointsSelected, null);
        this.areaSelection = new AreaSelection(mapWrapper.featureLayers.logpoints.layerSource, this.onLogpointsSelected);

        this.setSelectionMode(this.props.selectMode);

        // preselect logpoints when called with initial logpoints selection
        if (this.props.logpoints?.length) {
            this.onLogpointsSelected(this.props.logpoints)

            if (this.props.selectMode == 'click') {
                this.clickSelection.select(this.props.logpoints);
            }
        }
    }

    setSelectionMode(selectMode: SelectMode) {
        if (selectMode == 'click') {
            mapTools.activateTool(this.clickSelection);
        }
        else if (selectMode == 'area') {
            mapTools.activateTool(this.areaSelection);
        }

        this.setState({
            selectMode,
        });
    }

    componentWillUnmount() {
        this.clickSelection.deactivate();
        this.areaSelection.deactivate();
        mapWrapper.featureLayers.logpoints.highlightFeatures([]);
    }

    render() {
        const { logpoints } = this.state;

        if (!logpoints || logpoints.length === 0) {
            return <SelectionModePicker
                value={this.state.selectMode}
                onChange={mode => this.setSelectionMode(mode)}
                onClose={LogpointsEditor2dMap.close}
            />;
        }

        return <LogpointsEditor
            logpoints={this.state.logpoints}
            treeData={this.props.treeData}
            onClose={LogpointsEditor2dMap.close}
            onSaveSuccess={this.onSaveSuccess}
            onDeleteSuccess={this.onDeleteSuccess}
        >
        </LogpointsEditor>;
    }
}
